import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../../../../services/api';
import { Sun, Moon, Search, Filter, User, Plus, X, CalendarPlus } from 'lucide-react';
import type {
    BoardMenuApplyResult,
    BoardMenuProps as KanbanBoardMenuProps,
    DynamicFilterFieldValue,
    DynamicFilterInputValues,
    KanbanFilterColumn,
    KanbanFilterFieldSchema
} from '../../types';
import { useDynamicFilters } from '../../hooks/useDynamicFilters';
import {
    BoardTitle,
    FloatingActionButton,
    FloatingMenu,
    FloatingMenuOverlay,
    MenuItemContainer,
    SearchInputContainer,
    SearchInput,
    FilterButton,
    ThemeToggleButton,
    FilterPanel,
    FilterHeader,
    FilterSection,
    FilterSectionTitle,
    RadioList,
    RadioOption,
    ApplyFiltersButton,
    ApplyWrapper,
    ApplyBackdrop,
} from '../../styles';
import {
    FilterMenuContainer,
    ClearAllFiltersButton,
    FiltersLoadingMessage,
    DangerFilterButton,
} from './styles';
import {
    loadKanbanFilterPreferences,
    saveKanbanFilterPreferences,
    clearKanbanFilterPreferences,
} from '../../utils/kanbanFilterPreferences';

interface BoardMenuComponentProps extends KanbanBoardMenuProps {
    onClearFilters?: () => void;
    onRefreshBoard?: () => void;
    hasActiveFilters?: boolean;
}

const BoardMenu: React.FC<BoardMenuComponentProps> = ({
    boardTitle,
    boardId,
    currentTheme,
    onThemeToggle,
    showMyCards,
    onMyCardsToggle,
    onSearchTermChange,
    onApplyFilters,
    onClearFilters,
    onRefreshBoard,
    onOpenCreateFromSchedule,
    canCreateFromSchedule,
    hasActiveFilters,
}) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filterActive, setFilterActive] = useState<boolean>(false);
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [selectedColumns, setSelectedColumns] = useState<number[]>([]);
    const [fieldValues, setFieldValues] = useState<Record<string, DynamicFilterFieldValue>>({});
    const menuRef = useRef<HTMLDivElement>(null);
    const filterPrefsHydratedRef = useRef(false);

    const {
        columns,
        fields,
        loading: filtersLoading,
        buildPayload,
        getDefaultValues,
        normalizePersistedValues,
        isValueEmpty,
        FieldComponents,
    } = useDynamicFilters(boardId);

    useEffect(() => {
        filterPrefsHydratedRef.current = false;
    }, [boardId]);

    useEffect(() => {
        if (!boardId || filtersLoading || filterPrefsHydratedRef.current) return;
        const saved = loadKanbanFilterPreferences(boardId);
        filterPrefsHydratedRef.current = true;
        if (!saved) return;
        setSearchTerm(saved.search);
        setSelectedColumns(saved.selectedColumns);
        setFieldValues(normalizePersistedValues(saved.fieldValues));
        onSearchTermChange?.(saved.search);
    }, [boardId, filtersLoading, normalizePersistedValues, onSearchTermChange]);

    useEffect(() => {
        if (!filterActive || filtersLoading) return;

        const saved = loadKanbanFilterPreferences(boardId);
        const defaults = getDefaultValues();

        if (saved) {
            setSearchTerm(saved.search);
            setSelectedColumns(saved.selectedColumns);
            setFieldValues(normalizePersistedValues(saved.fieldValues));
            onSearchTermChange?.(saved.search);
        } else {
            setFieldValues(defaults);
            setSelectedColumns([]);
        }
    }, [filterActive, filtersLoading, boardId, getDefaultValues, normalizePersistedValues, onSearchTermChange]);

    const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    }, []);

    const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && onSearchTermChange) {
            onSearchTermChange(searchTerm);
        }
    }, [searchTerm, onSearchTermChange]);

    const handleFilterClick = useCallback(() => {
        setFilterActive(prev => !prev);
    }, []);

    const toggleMenu = useCallback(() => {
        setMenuOpen(prev => !prev);
    }, []);

    const handleApply = useCallback(async () => {
        if (!boardId) return;

        const payload = buildPayload({
            search: searchTerm,
            selectedColumns,
            ...fieldValues,
        });

        if (process.env.NODE_ENV === 'development') {
            console.debug('[Kanban filter] apply payload', {
                board_id: payload.board_id,
                column_ids: payload.column_ids,
                filtersCount: payload.filters?.length ?? 0,
                search: payload.search,
            });
        }

        if (!payload.search && (!payload.filters || payload.filters.length === 0) && (!payload.column_ids || payload.column_ids.length === 0)) {
            return;
        }

        try {
            const response = await api.post('/api/adm/cards/search', payload);

            let cardsData: any[] = [];

            if (response.data?.success && response.data?.data) {
                if (response.data.data.data && Array.isArray(response.data.data.data)) {
                    cardsData = response.data.data.data;
                } else if (Array.isArray(response.data.data)) {
                    cardsData = response.data.data;
                }
            } else if (Array.isArray(response.data)) {
                cardsData = response.data;
            } else if (response.data?.cards && Array.isArray(response.data.cards)) {
                cardsData = response.data.cards;
            }

            cardsData = Array.isArray(cardsData) ? cardsData : [];

            const result: BoardMenuApplyResult = {
                payload,
                cards: cardsData,
                rawResponse: response.data,
            };

            if (onApplyFilters) {
                onApplyFilters(result);
            }
            saveKanbanFilterPreferences(boardId, {
                search: searchTerm,
                selectedColumns,
                fieldValues: fieldValues as Record<string, unknown>,
            });
            setFilterActive(false);
        } catch (error) {
            if (onApplyFilters) {
                onApplyFilters({
                    payload,
                    cards: [],
                    rawResponse: null
                });
            }
        }
    }, [boardId, searchTerm, selectedColumns, fieldValues, buildPayload, onApplyFilters]);

    const handleClearAllFilters = useCallback(() => {
        clearKanbanFilterPreferences(boardId);
        setSearchTerm('');
        setSelectedColumns([]);
        setFieldValues(getDefaultValues());
        onSearchTermChange?.('');
    }, [getDefaultValues, boardId, onSearchTermChange]);

    const hasAnyFilterSelected = useCallback((): boolean => {
        if (selectedColumns.length > 0) return true;
        return Object.values(fieldValues).some(value => !isValueEmpty(value));
    }, [selectedColumns, fieldValues, isValueEmpty]);

    const handleColumnChange = useCallback((column: KanbanFilterColumn) => {
        setSelectedColumns(prev =>
            prev.includes(column.id)
                ? prev.filter(id => id !== column.id)
                : [...prev, column.id]
        );
    }, []);

    const handleFieldValueChange = useCallback((fieldKey: string, value: DynamicFilterFieldValue) => {
        setFieldValues(prev => ({
            ...prev,
            [fieldKey]: value,
        }));
    }, []);

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                closeMenu();
            }
        };

        if (menuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuOpen, closeMenu]);

    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                closeMenu();
            }
        };

        if (menuOpen) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [menuOpen, closeMenu]);

    return (
        <>
            {menuOpen && <FloatingMenuOverlay onClick={closeMenu} />}
            <div ref={menuRef}>
                <FloatingActionButton onClick={toggleMenu} isOpen={menuOpen}>
                    {menuOpen ? <X className="h-4 w-4" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
                </FloatingActionButton>

                <FloatingMenu isOpen={menuOpen} expanded={filterActive}>
                    <MenuItemContainer>
                        <BoardTitle>{boardTitle}</BoardTitle>
                    </MenuItemContainer>

                    <MenuItemContainer>
                        <SearchInputContainer>
                            <Search className="h-3.5 w-3.5" aria-hidden />
                            <SearchInput
                                type="text"
                                placeholder="Pesquisar cards..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                onKeyDown={handleSearchKeyDown}
                            />
                        </SearchInputContainer>
                    </MenuItemContainer>

                    <MenuItemContainer>
                        <FilterButton onClick={handleFilterClick} isActive={filterActive}>
                            <Filter className="h-3.5 w-3.5" aria-hidden />
                            Filtros
                        </FilterButton>
                    </MenuItemContainer>

                    {filterActive && (
                        <FilterMenuContainer>
                            <FilterPanel>
                                <FilterHeader>
                                    Filtros
                                    {hasAnyFilterSelected() && (
                                        <ClearAllFiltersButton onClick={handleClearAllFilters}>
                                            Limpar todos
                                        </ClearAllFiltersButton>
                                    )}
                                </FilterHeader>

                                {filtersLoading && (
                                    <FiltersLoadingMessage>
                                        Carregando filtros...
                                    </FiltersLoadingMessage>
                                )}

                                {!filtersLoading && (
                                    <>
                                        {columns.length > 0 && (
                                            <FilterSection>
                                                <FilterSectionTitle>Colunas</FilterSectionTitle>
                                                <RadioList>
                                                    {columns.map((column: KanbanFilterColumn) => (
                                                        <RadioOption key={column.id}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedColumns.includes(column.id)}
                                                                onChange={() => handleColumnChange(column)}
                                                            />
                                                            <span>{column.name}</span>
                                                        </RadioOption>
                                                    ))}
                                                </RadioList>
                                            </FilterSection>
                                        )}

                                        {fields
                                            .slice()
                                            .sort((a: KanbanFilterFieldSchema, b: KanbanFilterFieldSchema) => a.order - b.order)
                                            .map((field: KanbanFilterFieldSchema) => {
                                                const fieldKey = `field_${field.id}`;
                                                const FieldComponent = FieldComponents[fieldKey];

                                                if (!FieldComponent) return null;

                                                return (
                                                    <FieldComponent
                                                        key={field.id}
                                                        field={field}
                                                        value={fieldValues[fieldKey]}
                                                        onChange={(value: DynamicFilterFieldValue) => handleFieldValueChange(fieldKey, value)}
                                                    />
                                                );
                                            })}
                                    </>
                                )}
                            </FilterPanel>

                            {hasAnyFilterSelected() && (
                                <>
                                    <ApplyBackdrop />
                                    <ApplyWrapper>
                                        <ApplyFiltersButton onClick={handleApply}>
                                            Aplicar Filtros
                                        </ApplyFiltersButton>
                                    </ApplyWrapper>
                                </>
                            )}
                        </FilterMenuContainer>
                    )}

                    {canCreateFromSchedule && (
                        <MenuItemContainer>
                            <FilterButton
                                onClick={() => {
                                    closeMenu();
                                    onOpenCreateFromSchedule?.();
                                }}
                            >
                                <CalendarPlus className="h-3.5 w-3.5" aria-hidden />
                                Criar card de agendamento
                            </FilterButton>
                        </MenuItemContainer>
                    )}

                    <MenuItemContainer>
                        <FilterButton onClick={onMyCardsToggle}>
                            <User className="h-3.5 w-3.5" aria-hidden />
                            {showMyCards ? 'Todos os cards' : 'Meus cards'}
                        </FilterButton>
                    </MenuItemContainer>

                    {hasActiveFilters && (
                        <MenuItemContainer>
                            <DangerFilterButton onClick={onClearFilters} isActive={false}>
                                <X className="h-3.5 w-3.5" aria-hidden />
                                Limpar filtros
                            </DangerFilterButton>
                        </MenuItemContainer>
                    )}

                    <MenuItemContainer>
                        <ThemeToggleButton onClick={onThemeToggle}>
                            {currentTheme === 'light' ? <Moon className="h-4 w-4" aria-hidden /> : <Sun className="h-4 w-4" aria-hidden />}
                            {currentTheme === 'light' ? 'Modo escuro' : 'Modo claro'}
                        </ThemeToggleButton>
                    </MenuItemContainer>
                </FloatingMenu>
            </div>
        </>
    );
};

export default BoardMenu;